import {
  fetchChannelsApi,
  fetchDisplaySettingsApi,
  fetchSecuritySettingsApi,
  fetchWebsiteApi,
  fetchTermsAndConditionsApi,
  fetchUserApi,
} from 'common/api/index';
import { runRootSaga, store } from 'store';
import {
  displaySettingsFetchSuccessAction,
  resizeWindowAction,
  setPlayerAnalyticsConsentAction,
  websiteFetchSuccessAction,
} from 'common/actions/index';
import {
  assetsSelector,
  displaySettingsSelector,
  getRelatedVideosFeatureSelector,
  viewRangeSelector,
} from 'common/selectors/index';
import { RESPONSIVE_RANGES } from 'utils/enums';
import { getResponsiveRange, getWidth, isParentAllowed } from 'utils/index';
import {
  usercentricsContentChangeEventListener,
  usercentricsServiceNames,
} from 'utils/usercentrics-content-change-event-listener';
import queryString from 'query-string';
import { createRoot } from 'react-dom/client';

import { AppProvider } from 'app.provider';
import { config } from 'config';
import i18next from 'i18next';
import { channelsSuccessAction } from 'common/actions/channels';
import { getI18nLng, supportedLanguages } from 'utils/i18n';
import { consentExpiredOrNotSet } from './components/terms-and-conditions/terms-and-conditions-utils';
import { openTermsAndConditions } from './components/terms-and-conditions/index';
import { getUserId } from './utils/website';
import { termsAndConditionsFetchSuccessAction } from 'common/actions/termsAndConditions';
import { currentUserAction } from 'common/actions';

import { isTermsAndConditionsObject } from './components/terms-and-conditions/terms-and-conditions-utils';
import { CtPrivacyRepository } from 'common/api/models';
import { usercentricsConsentWasGiven } from 'utils/usercentrics-service-consent';
import { initUsercentrics, usercentricsEnabled } from 'utils/usercentrics';

const container = document.getElementById('root');
export const root = createRoot(container);

const dispatchRange = () => {
  const isDevice = /(Android|iPhone|iPad)/g.exec(navigator.userAgent) !== null;
  store.dispatch(
    resizeWindowAction({
      size: getWidth(),
      range: getResponsiveRange(),
      isDevice,
    })
  );
};

const initStore = async () => {
  dispatchRange();
  const channels = await fetchChannelsApi();
  store.dispatch(channelsSuccessAction(channels));

  const displaySettings = await fetchDisplaySettingsApi();
  const website = await fetchWebsiteApi();
  store.dispatch(websiteFetchSuccessAction(website));

  const termsAndConditions = await fetchTermsAndConditionsApi();

  if (isTermsAndConditionsObject(termsAndConditions)) {
    store.dispatch(termsAndConditionsFetchSuccessAction(termsAndConditions));
  }

  if (CtPrivacyRepository.isPrivate()) {
    const userId = getUserId();
    const user = await fetchUserApi(userId);
    store.dispatch(currentUserAction({ id: userId, watermark: user.watermark }));
  }

  const related = getRelatedVideosFeatureSelector({
    d_settings: {
      display: displaySettings,
    },
  });
  const viewRange = viewRangeSelector(store.getState());
  if (viewRange === RESPONSIVE_RANGES.DESKTOP_LARGE && related) {
    displaySettings.videosPerPage = 15;
  } else {
    displaySettings.videosPerPage = 12;
  }

  store.dispatch(displaySettingsFetchSuccessAction(displaySettings));

  if (isTermsAndConditionsObject(termsAndConditions)) {
    checkTermsAndConditionsConsent(store);
  }
};

const checkSecurity = async () => {
  const displaySettings = displaySettingsSelector(store.getState());
  const secSettings = await fetchSecuritySettingsApi();
  if (!isParentAllowed(secSettings, displaySettings, queryString.parse(window.location.search))) {
    document.getElementById('root').innerHTML = '<h1>Forbidden.</h1>';
    return;
  }
};

const renderApp = () => {
  window.addEventListener('resize', () => dispatchRange(store));

  root.render(AppProvider(store));
};

const appendCSS = () => {
  const { NODE_ENV } = process.env;
  const { mainCssRemoteInLocalDev } = config;

  if (NODE_ENV === 'development' && !mainCssRemoteInLocalDev) {
    require('./index.css');
    return renderApp();
  }

  const assets = assetsSelector(store.getState());
  const stylesheet = document.createElement('link');
  stylesheet.setAttribute('rel', 'stylesheet');
  stylesheet.setAttribute('type', 'text/css');
  stylesheet.setAttribute('href', assets.mainCss.url);

  document.head.append(stylesheet);

  const css = document.head.querySelector(`link[href="${assets.mainCss.url}"]`);
  css.addEventListener('load', renderApp);
};

const handleLocalization = () => {
  const { languages } = displaySettingsSelector(store.getState());

  if (languages.length === 1) {
    const firstLng = languages[0];
    const lng = supportedLanguages.includes(firstLng) ? firstLng : 'en';

    i18next.changeLanguage(lng);
  } else if (!languages.includes(getI18nLng())) {
    i18next.changeLanguage('en');
  }
};

const checkTermsAndConditionsConsent = (store) => {
  const userId = getUserId();

  if (consentExpiredOrNotSet(userId)) {
    store.dispatch(openTermsAndConditions());
  }
};

export const initApp = async () => {
  if (usercentricsEnabled()) {
    //...
  }

  runRootSaga();
  await initStore();
  await checkSecurity();
  if (usercentricsEnabled()) {
    // init usercentrics before rendering the app
    await initUsercentrics(window.__MOVINGIMAGE_CT_USERCENTRICS_CONFIG);
  }
  await appendCSS();
  handleLocalization();

  if (usercentricsEnabled()) {
    store.dispatch(
      setPlayerAnalyticsConsentAction(usercentricsConsentWasGiven(usercentricsServiceNames.playerAnalytics))
    );

    usercentricsContentChangeEventListener();
  } else {
    // if usercentrics is not set, we assume that the user has given consent
    store.dispatch(setPlayerAnalyticsConsentAction(true));
  }
};
